import { useState } from "react";
import { useTranslation } from "react-i18next";

const isBlank = (value) => !value || (typeof value === "string" && !value.trim());

// Formats documentés par le contrat Partners API (partnersApi.yaml).
const RIB_PATTERN = /^[0-9]{24}$/; // financialInformation.rib : "RIB - 24 digits"
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // legalRepresentative.email : format email

const FIELD_LABELS = {
  legalName: "manageEstablishment.new.fields.companyName",
  rcNumber: "manageEstablishment.new.fields.rcNumber",
  tribunalCity: "manageEstablishment.new.fields.tribunalCity",
  lastName: "manageEstablishment.new.fields.lastName",
  firstName: "manageEstablishment.new.fields.firstName",
  identityDocumentType: "manageEstablishment.new.fields.identityType",
  identityNumber: "manageEstablishment.new.fields.identityNumber",
  identityExpiryDate: "manageEstablishment.new.fields.identityExpiry",
  email: "manageEstablishment.new.fields.professionalEmail",
  phoneNumber: "manageEstablishment.new.fields.phoneNumber",
  ownershipPercentage: "manageEstablishment.new.fields.capitalShare",
  nationality: "manageEstablishment.new.fields.nationality",
  bankName: "manageEstablishment.new.fields.bank",
  accountHolder: "manageEstablishment.new.fields.accountHolder",
  rib: "manageEstablishment.new.fields.rib",
};

// Les règles ci-dessous reprennent celles de la Partners API (schémas `company`,
// `commercialRegister`, `legalRepresentative`, `financialInformation`) : un champ
// n'est marqué obligatoire côté portail que s'il l'est côté API.
const useFormValidation = () => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});

  const requireField = (validationErrors, key) => {
    validationErrors[key] = [
      t("commons.errors.requiredField", { fieldName: t(FIELD_LABELS[key]) }),
    ];
  };

  const finish = (validationErrors) => {
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  // Rubrique Établissement : seule la raison sociale est obligatoire. Le registre
  // du commerce est facultatif, mais dès qu'il est envoyé number et courtCity le
  // deviennent. L'adresse et les agréments n'ont aucun champ obligatoire.
  const validate = (values) => {
    const validationErrors = {};

    if (isBlank(values.company?.legalName)) requireField(validationErrors, "legalName");

    const commercialRegister = values.company?.commercialRegister;
    if (commercialRegister) {
      if (isBlank(commercialRegister.number)) requireField(validationErrors, "rcNumber");
      if (isBlank(commercialRegister.courtCity)) requireField(validationErrors, "tribunalCity");
    }

    return finish(validationErrors);
  };

  // Rubrique Gouvernance : tous les champs du représentant légal sont obligatoires,
  // sauf le justificatif (supportingDocumentsId). Le téléphone doit fournir
  // indicative + number ; son format est contrôlé par usePhoneValidation.
  const validateLegalRepresentative = (values) => {
    const validationErrors = {};

    for (const key of [
      "lastName",
      "firstName",
      "identityDocumentType",
      "identityNumber",
      "identityExpiryDate",
      "nationality",
    ]) {
      if (isBlank(values[key])) requireField(validationErrors, key);
    }

    if (isBlank(values.email)) {
      requireField(validationErrors, "email");
    } else if (!EMAIL_PATTERN.test(String(values.email).trim())) {
      validationErrors.email = [
        t("manageEstablishment.new.errors.emailInvalid", "L'adresse email est invalide."),
      ];
    }

    if (!values.phoneNumber) requireField(validationErrors, "phoneNumber");

    const percentage = values.ownershipPercentage;
    if (percentage === null || percentage === undefined || Number.isNaN(percentage)) {
      requireField(validationErrors, "ownershipPercentage");
    }

    return finish(validationErrors);
  };

  // Rubrique Finances : bankName, accountHolder et rib sont obligatoires ; le RIB
  // doit compter exactement 24 chiffres.
  const validateFinancialInformation = (values) => {
    const validationErrors = {};

    if (isBlank(values.bankName)) requireField(validationErrors, "bankName");
    if (isBlank(values.accountHolder)) requireField(validationErrors, "accountHolder");

    if (isBlank(values.rib)) {
      requireField(validationErrors, "rib");
    } else if (!RIB_PATTERN.test(String(values.rib).trim())) {
      validationErrors.rib = [
        t("manageEstablishment.new.errors.ribInvalid", "Le RIB doit contenir exactement 24 chiffres."),
      ];
    }

    return finish(validationErrors);
  };

  return {
    errors,
    validate,
    validateLegalRepresentative,
    validateFinancialInformation,
    clearErrors: () => setErrors({}),
  };
};

export default useFormValidation;
